/* Groepsapp — mobiel-first view voor pedagogisch medewerkers.
   Toont per kind grote cards met snel-acties: presentie, +maaltijd,
   +slaap, +luier, schriftje/chat. */
import { localToday } from "@/lib/format";
import { useEffect, useState, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { submitOrQueue } from "@/lib/offlineQueue";
import { pendingCount, flushQueue } from "@/lib/offlineQueue";
import PhotoUploadButton from "@/components/PhotoUploadButton";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Baby, LogOut, Check, X, Utensils, Bed, MessageCircle, Trash2, BookOpen, Sparkles, LogIn, Clock, CalendarClock, CloudOff, RefreshCw } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import ChatDialog from "@/components/ChatDialog";
import BirthdayWidget from "@/components/BirthdayWidget";
import ReactionsFeed from "@/components/ReactionsFeed";
import WeekplanEditor from "@/components/WeekplanEditor";
import SwipeableKindCard from "@/components/SwipeableKindCard";

const TYPE_ICON = {
  maaltijd: { icon: Utensils, color: "text-amber-700 bg-amber-50", label: "Maaltijd" },
  slaap_start: { icon: Bed, color: "text-blue-700 bg-blue-50", label: "Slaap start" },
  slaap_eind: { icon: Bed, color: "text-emerald-700 bg-emerald-50", label: "Wakker" },
  luier: { icon: Baby, color: "text-purple-700 bg-purple-50", label: "Luier" },
  toilet: { icon: Baby, color: "text-purple-700 bg-purple-50", label: "Toilet" },
  activiteit: { icon: BookOpen, color: "text-[color:var(--sage)] bg-[color:var(--sage-100)]", label: "Activiteit" },
  notitie: { icon: BookOpen, color: "text-neutral-700 bg-neutral-100", label: "Notitie" },
};

export default function GroepsappPage() {
  const { user, logout, status } = useAuth();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("all");
  const [datum] = useState(localToday());
  const [data, setData] = useState(null);
  const [chatKid, setChatKid] = useState(null);
  const [selectedKid, setSelectedKid] = useState(null);
  const [dagLog, setDagLog] = useState([]);
  const [recentSchriftjes, setRecentSchriftjes] = useState([]);

  // Alleen team-rollen mogen hierin — ouder wordt naar /portaal geleid, leidster naar Presentielijst
  const isTeam = user && user.role !== "ouder";
  // pedagogisch: filter groepen op eigen scope
  const visibleGroups = user?.role === "pedagogisch" && user?.group_ids?.length
    ? groups.filter(g => user.group_ids.includes(g.id))
    : groups;

  const load = useCallback(async () => {
    try {
      const params = { datum };
      if (groupId !== "all") params.group_id = groupId;
      const r = await api.get("/planning/day", { params });
      setData(r.data);
    } catch (e) {
      // stille fallback bij 403 — laat lege state zien
      setData({ items: [] });
    }
  }, [datum, groupId]);

  useEffect(() => {
    if (status !== "authed" || !isTeam) return;
    api.get("/groups").then(r => setGroups(r.data)).catch(() => setGroups([]));
  }, [status, isTeam]);
  useEffect(() => { if (status === "authed" && isTeam) load(); }, [status, isTeam, load]);

  const loadLog = useCallback(async (kindId) => {
    if (!kindId) { setDagLog([]); setRecentSchriftjes([]); return; }
    try {
      const [logR, schR] = await Promise.all([
        api.get("/daglog", { params: { kind_id: kindId, datum } }),
        api.get("/schriftje", { params: { kind_id: kindId, limit: 3 } }),
      ]);
      setDagLog(logR.data || []);
      setRecentSchriftjes(schR.data || []);
    } catch (e) {
      setDagLog([]);
      setRecentSchriftjes([]);
    }
  }, [datum]);

  useEffect(() => { loadLog(selectedKid?.id); }, [selectedKid, loadLog]);

  // Offline queue-teller in header
  const [pending, setPending] = useState(0);
  const [isOnline, setIsOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  useEffect(() => {
    let mounted = true;
    const refresh = async () => { const n = await pendingCount(); if (mounted) setPending(n); };
    refresh();
    const onChange = () => refresh();
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener("offline-queue-changed", onChange);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      mounted = false;
      window.removeEventListener("offline-queue-changed", onChange);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  if (status === "loading") return <div className="min-h-screen flex items-center justify-center">Laden…</div>;
  if (status === "anon") return <Navigate to="/login" replace />;
  if (user?.role === "ouder") return <Navigate to="/portaal" replace />;
  if (user?.role === "pedagogisch") return <Navigate to="/app/planning" replace />;

  const togglePresent = async (item) => {
    const cur = item.presentie?.aanwezig ?? true;
    try {
      await api.post("/planning/presentie", {
        kind_id: item.kind.id, group_id: item.group_id, datum,
        dagdeel: item.dagdeel, aanwezig: !cur,
      });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const nowHHMM = () => {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };

  const aanmelden = async (item) => {
    try {
      const t = nowHHMM();
      const r = await submitOrQueue({
        url: "/planning/presentie",
        data: {
          kind_id: item.kind.id, group_id: item.group_id, datum,
          dagdeel: item.dagdeel, aanwezig: true, aankomst: t,
        },
        label: `Aanmelden ${item.kind.voornaam}`,
      });
      if (r.queued) toast.info(`${item.kind.voornaam} offline aangemeld — synct zodra je online bent`);
      else toast.success(`${item.kind.voornaam} aangemeld om ${t}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const afmelden = async (item) => {
    if (!item.presentie?.aankomst) {
      toast.error("Afmelden kan pas nadat het kind is aangemeld");
      return;
    }
    try {
      const t = nowHHMM();
      const r = await submitOrQueue({
        url: "/planning/presentie",
        data: {
          kind_id: item.kind.id, group_id: item.group_id, datum,
          dagdeel: item.dagdeel, aanwezig: false, vertrek: t,
        },
        label: `Afmelden ${item.kind.voornaam}`,
      });
      if (r.queued) toast.info(`${item.kind.voornaam} offline afgemeld — synct zodra je online bent`);
      else toast.success(`${item.kind.voornaam} afgemeld om ${t}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const quickLog = async (kindId, type, details = "") => {
    try {
      const r = await submitOrQueue({
        url: "/daglog",
        data: { kind_id: kindId, type, details },
        label: `Daglog ${type}`,
      });
      if (r.queued) toast.info("Daglog offline opgeslagen — synct zodra je online bent");
      else toast.success(`${type.replace('_', ' ')} vastgelegd`);
      loadLog(kindId);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeEvent = async (id) => {
    try {
      await api.delete(`/daglog/${id}`);
      loadLog(selectedKid?.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const items = data?.items || [];
  const initials = user?.name?.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase() || "T";

  return (
    <div className="min-h-screen bg-[color:var(--bone)] subtle-grain">
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: "var(--sage)" }}>
              <Baby className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="font-display text-sm leading-tight">Webster<span className="text-[color:var(--terracotta)]">.</span></div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Groepsapp</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(!isOnline || pending > 0) && (
              <button
                onClick={async () => { const r = await flushQueue(); toast.info(`Sync: ${r.sent} verstuurd${r.failed ? `, ${r.failed} mislukt` : ""}`); }}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium border ${isOnline ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-red-50 border-red-300 text-red-800"}`}
                data-testid="groepsapp-offline-chip"
                title={isOnline ? "Wachtend op sync — klik om nu te synchroniseren" : "Offline — acties worden opgeslagen"}
              >
                {isOnline ? <RefreshCw className="h-3.5 w-3.5" /> : <CloudOff className="h-3.5 w-3.5" />}
                <span>{isOnline ? `${pending} wachtend` : `Offline · ${pending}`}</span>
              </button>
            )}
            <Button variant="ghost" size="sm" onClick={() => navigate("/app/planning")} data-testid="groepsapp-back">Naar Presentielijst</Button>
            <Avatar className="h-8 w-8"><AvatarFallback className="bg-[color:var(--sage)] text-white text-xs">{initials}</AvatarFallback></Avatar>
            <Button variant="ghost" size="icon" data-testid="groepsapp-logout" onClick={async () => { await logout(); navigate("/login"); }}><LogOut className="h-4 w-4" /></Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-4 pb-24 lg:pb-4 space-y-3">
        <div>
          <h1 className="font-display text-2xl tracking-tight" data-testid="groepsapp-title">Vandaag · {new Date(datum).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}</h1>
          <Select value={groupId} onValueChange={setGroupId}>
            <SelectTrigger className="mt-2" data-testid="groepsapp-group"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Alle groepen</SelectItem>
              {visibleGroups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {items.length === 0 && <div className="text-sm text-neutral-500 p-6 text-center">Geen kinderen gepland vandaag.</div>}

        <BirthdayWidget items={items} />

        <ReactionsFeed limit={6} />

        <WeekplanEditor groups={visibleGroups} activeGroupId={groupId !== "all" ? groupId : undefined} />

        {items.map(it => {
          const p = it.presentie;
          const isAangemeld = !!p?.aankomst;
          const isAfgemeld = isAangemeld && p?.vertrek && !p?.aanwezig;
          const isOpen = selectedKid?.id === it.kind.id;
          return (
            <SwipeableKindCard
              key={it.kind.id}
              className="p-4"
              data-testid={`groepsapp-kind-${it.kind.id}`}
              onSwipeRight={!isAangemeld ? () => aanmelden(it) : undefined}
              onSwipeLeft={isAangemeld && !isAfgemeld ? () => afmelden(it) : undefined}
              swipeRightLabel="Aanmelden"
              swipeLeftLabel="Afmelden"
            >
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSelectedKid(isOpen ? null : it.kind)}
                  data-testid={`groepsapp-avatar-${it.kind.id}`}
                  className="w-11 h-11 rounded-full flex items-center justify-center bg-[color:var(--sage-100)] text-[color:var(--sage)] font-semibold shrink-0"
                >
                  {it.kind.voornaam[0]}{it.kind.achternaam[0]}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-neutral-800 truncate flex items-center gap-1.5">
                    {it.kind.voornaam} {it.kind.achternaam}
                    {isAfgemeld && <Badge variant="outline" className="bg-neutral-100 text-neutral-600 text-[10px]">Afgemeld</Badge>}
                    {isAangemeld && !isAfgemeld && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Aangemeld</Badge>}
                  </div>
                  <div className="text-xs text-neutral-500 truncate flex items-center gap-2">
                    {it.mentor && <span>mentor: {it.mentor.voornaam} {it.mentor.achternaam}</span>}
                    {(p?.aankomst || p?.vertrek) && (
                      <span className="flex items-center gap-1 text-neutral-600" data-testid={`groepsapp-tijden-${it.kind.id}`}>
                        <Clock className="h-3 w-3" />
                        {p?.aankomst || "--:--"}{p?.vertrek ? ` → ${p.vertrek}` : ""}
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setChatKid(it.kind.id)}
                  className="relative"
                  data-testid={`groepsapp-chat-${it.kind.id}`}
                >
                  <MessageCircle className="h-4 w-4" />
                  {it.unread_chat > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-4 rounded-full bg-[color:var(--terracotta)] text-white text-[10px] flex items-center justify-center px-1 font-semibold">
                      {it.unread_chat}
                    </span>
                  )}
                </Button>
                {!isAangemeld && (
                  <Button
                    size="sm"
                    onClick={() => aanmelden(it)}
                    data-testid={`groepsapp-aanmelden-${it.kind.id}`}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white h-11 px-4 min-w-[120px]"
                  >
                    <LogIn className="h-4 w-4 mr-1.5" /> Aanmelden
                  </Button>
                )}
                {isAangemeld && !isAfgemeld && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => afmelden(it)}
                    data-testid={`groepsapp-afmelden-${it.kind.id}`}
                    className="border-[color:var(--terracotta)] text-[color:var(--terracotta)] hover:bg-[color:var(--terracotta)]/10 h-11 px-4 min-w-[120px]"
                  >
                    <LogOut className="h-4 w-4 mr-1.5" /> Afmelden
                  </Button>
                )}
                {isAfgemeld && (
                  <Badge variant="outline" className="bg-neutral-100 text-neutral-500 text-[10px] h-11 flex items-center px-3" data-testid={`groepsapp-afgemeld-badge-${it.kind.id}`}>
                    <Check className="h-3 w-3 mr-1" /> Klaar
                  </Badge>
                )}
              </div>

              {isOpen && (
                <div className="mt-3 pt-3 border-t border-neutral-100 space-y-3" data-testid={`groepsapp-panel-${it.kind.id}`}>
                  {!isAangemeld ? (
                    <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3" data-testid={`groepsapp-notaangemeld-${it.kind.id}`}>
                      <div className="font-medium mb-0.5">Kind is niet aangemeld</div>
                      <div className="text-xs text-red-600">Meld {it.kind.voornaam} eerst aan voordat je een schriftje, foto of daglog maakt.</div>
                    </div>
                  ) : (
                  <><div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => quickLog(it.kind.id, "maaltijd", "Fruithapje")}
                      data-testid={`quicklog-maaltijd-${it.kind.id}`}
                      style={{ background: "var(--sage)" }} className="text-white">
                      <Utensils className="h-3 w-3 mr-1" /> +Maaltijd
                    </Button>
                    <Button size="sm" onClick={() => quickLog(it.kind.id, "slaap_start")}
                      data-testid={`quicklog-slaap-${it.kind.id}`}
                      variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-50">
                      <Bed className="h-3 w-3 mr-1" /> +Slaap start
                    </Button>
                    <Button size="sm" onClick={() => quickLog(it.kind.id, "slaap_eind")}
                      data-testid={`quicklog-wakker-${it.kind.id}`}
                      variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                      <Bed className="h-3 w-3 mr-1" /> +Wakker
                    </Button>
                    <Button size="sm" onClick={() => quickLog(it.kind.id, "activiteit", "Buitenspelen")}
                      data-testid={`quicklog-activiteit-${it.kind.id}`}
                      variant="outline" className="border-purple-200 text-purple-700 hover:bg-purple-50">
                      <Baby className="h-3 w-3 mr-1" /> +Activiteit
                    </Button>
                    <Button size="sm"
                      onClick={() => navigate(`/app/communicatie?kind=${it.kind.id}`)}
                      data-testid={`quicklog-schriftje-${it.kind.id}`}
                      variant="outline" className="border-[color:var(--terracotta)] text-[color:var(--terracotta)] hover:bg-[color:var(--terracotta)]/5">
                      <Sparkles className="h-3 w-3 mr-1" /> AI-schriftje
                    </Button>
                    <PhotoUploadButton
                      kindIds={[it.kind.id]}
                      label="Foto"
                      size="sm"
                    />
                  </div>

                  {recentSchriftjes.length > 0 && (
                    <div className="space-y-1" data-testid={`groepsapp-schriftjes-${it.kind.id}`}>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
                        Recente schriftjes ({recentSchriftjes.length})
                      </div>
                      {recentSchriftjes.map(s => (
                        <div key={s.id} className="p-2 rounded-md bg-[color:var(--sage-100)]/40 border border-[color:var(--sage-100)] text-xs" data-testid={`schriftje-item-${s.id}`}>
                          <div className="flex items-center justify-between mb-0.5">
                            <div className="font-semibold text-neutral-800 truncate flex items-center gap-1.5">
                              <BookOpen className="h-3 w-3 text-[color:var(--sage)]" />
                              {s.datum ? new Date(s.datum).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : '—'}
                            </div>
                            {s.stemming && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-white text-neutral-600 border border-neutral-200 capitalize">
                                {s.stemming}
                              </span>
                            )}
                          </div>
                          {(s.hoogtepunt || s.activiteiten || s.notitie_voor_ouder) && (
                            <div className="text-neutral-700 line-clamp-2">
                              {s.hoogtepunt || s.activiteiten || s.notitie_voor_ouder}
                            </div>
                          )}
                          {s.author_name && <div className="text-[10px] text-neutral-500 mt-0.5">— {s.author_name}</div>}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="space-y-1">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Vandaag ({dagLog.length})</div>
                    {dagLog.length === 0 && <div className="text-xs text-neutral-500 pl-1">Nog geen events vandaag.</div>}
                    {dagLog.map(e => {
                      const info = TYPE_ICON[e.type] || TYPE_ICON.notitie;
                      const Ic = info.icon;
                      return (
                        <div key={e.id} className="flex items-center gap-2 py-1.5 text-sm">
                          <span className="text-neutral-500 font-mono text-xs w-12">{e.tijdstip.slice(11, 16)}</span>
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${info.color}`}>
                            <Ic className="h-3 w-3" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-neutral-800">{info.label}</div>
                            {e.details && <div className="text-[11px] text-neutral-500 truncate">{e.details}</div>}
                          </div>
                          <button data-testid={`groepsapp-delete-${e.id}`} onClick={() => removeEvent(e.id)} className="text-neutral-400 hover:text-red-600 transition-colors">
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  </>
                  )}
                </div>
              )}
            </SwipeableKindCard>
          );
        })}
      </main>

      <ChatDialog kindId={chatKid} open={!!chatKid} onClose={() => setChatKid(null)} onSent={load} />
      <div className="text-center text-[11px] text-neutral-400 pb-6">
        Powered by <span className="font-semibold text-[color:var(--sage)]">Webster</span>
      </div>
    </div>
  );
}
